/** Third party imports */
import { DatePipe } from '@angular/common';
import { Component, computed, inject, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';

/** Component library imports */
import { Accordion } from '@ntv360/component-pantry';

/** Shared imports */
import { CreateUserStepsService } from '@core';
import { NAME_TERRITORY_LAYOUT } from '@features/users/constants';
import { CreateTerritoryStepRoles, DealerSteps } from '@features/users/interfaces';
import { TerritoryMapComponent } from '@shared/components';
import { debounceTime } from 'rxjs';

@Component({
    selector: 'app-name-territory',
    imports: [ReactiveFormsModule, TerritoryMapComponent, Accordion, DatePipe],
    templateUrl: './name-territory.component.html',
    styleUrl: './name-territory.component.scss',
})
export class NameTerritoryComponent implements OnInit {
    /**
     * Inject create user service to get access to the draft data
     */
    protected createUserStepsService = inject(CreateUserStepsService);

    /**
     * Layout constant
     */
    protected nameTerritoryLayout = NAME_TERRITORY_LAYOUT;

    /**
     * Form control for the territory name input
     */
    protected territoryNameControl = new FormControl('', {
        validators: [Validators.required],
    });

    /**
     * Selected user role to be created. For type inference
     */
    protected selectedUserType = this.createUserStepsService.selectedUserType;

    /**
     * Get create territory data
     */
    protected createTerritoryData = computed(() => {
        const draft = this.createUserStepsService.getDraft(
            this.selectedUserType()?.id as CreateTerritoryStepRoles,
        );
        return draft?.['create-territory'];
    });

    /**
     * Territory coordinates — reactive so it updates when the draft changes
     */
    protected territoryCoordinates = computed(() => {
        return this.createTerritoryData()?.coordinates || [];
    });

    /**
     * Get dealer information
     */
    protected dealerInfo = computed(() => {
        return this.createUserStepsService.getDraft('dealer')['input-information'];
    });

    /**
     * Populate territory name if present in draft
     */
    public ngOnInit(): void {
        this.territoryNameControl.setValue(this.createTerritoryData()?.territoryName ?? '');

        // Setup auto-save after 300ms when the user stops typing in the input field so when they leave the tab, their data gets saved
        // in the background
        this.territoryNameControl.valueChanges.pipe(debounceTime(300)).subscribe((value) => {
            this.createUserStepsService.patchDraftStep(
                this.selectedUserType()?.id as CreateTerritoryStepRoles,
                'create-territory',
                {
                    territoryName: value ?? '',
                },
            );

            // Enable the next step from getting clicked using the stepper clicks if the territory name is valid
            if (this.territoryNameControl.valid) {
                this.createUserStepsService.setValidStep();
            } else {
                this.createUserStepsService.setInvalidStep(
                    this.createUserStepsService.currentStepIndex(),
                );
            }
        });
    }

    /**
     * A method to validate the territory name
     * @public So we can call it in the very parent component when we click the next button `create-user.component.ts`
     */
    public validateTerritoryName(): boolean {
        this.territoryNameControl.markAsTouched();
        return this.territoryNameControl.valid;
    }

    /**
     * Returns the error message if there are any
     */
    public getErrorMessage(): string {
        if (this.territoryNameControl.touched) {
            if (this.territoryNameControl.errors?.['required']) {
                return 'Please enter a territory name';
            }
        }
        return '';
    }

    /**
     * Get detail value based on field
     * @param field The field to get value for
     * @returns The formatted value
     */
    protected getDetailValue(
        field: string,
    ): string | number | number[][] | Date | { marker: unknown; latLang: unknown }[] {
        const data = this.createTerritoryData() as DealerSteps['create-territory'];
        if (!data) return '';

        if (field === 'assignedTo') {
            return `${this.dealerInfo()?.firstName} ${this.dealerInfo()?.lastName}`;
        }

        return data[field as keyof typeof data] ?? '';
    }

    /**
     * Save territory name and move to the next step
     * @public
     */
    public saveTerritoryName(): void {
        const userRole = this.selectedUserType()?.id as CreateTerritoryStepRoles;
        const existingDraft = this.createUserStepsService.getDraft(userRole);
        const existingTerritoryData = existingDraft?.['create-territory'];

        const territoryName =
            this.territoryNameControl.value ?? this.createTerritoryData()?.territoryName;

        if (!existingTerritoryData) return;

        this.createUserStepsService.updateStep(userRole, 'create-territory', {
            ...existingTerritoryData,
            territoryName: territoryName ?? '',
            assignedTo:
                `${this.dealerInfo()?.firstName ?? ''} ${this.dealerInfo()?.lastName ?? ''}`.trim(),
        });
    }
}
